// @flow

import type { Organization } from '@gandi/redux-organization';
import { useTranslator, mapTags } from '@gandi/translate';
import { TextPlaceholder } from '@gandi/ui';

import { PillButton } from 'src/components/PillButton';

import { SetupPill } from '../SetupPill';

import { useOwner, type OwnProps as Props } from './hooks';

const resellerProducts = ['DomainCreate', 'CertificateCreate'] as const;

const toRenewProducts = [
  'DomainRenew',
  'MailboxRenew',
  'MailboxStandAloneRenew',
  'MailboxUpdate',
  'Mailboxv2Renew',
  'Mailboxv2Update',
  'MarketplaceRenew',
  'MarketplaceUpdate',
  'PaasRenew',
] as const;

const attachedToDomainProducts = [
  'MailboxCreate',
  'Mailboxv2Create',
  'PremiumDNSCreate',
] as const;

const otherProducts = [
  'CertificateCreate',
  'CloudAdd',
  'DomainCreate',
  'PaasCreate',
] as const;

function getOrgDisplayName(org: Organization) {
  if (!org) {
    return null;
  }

  if (org.type === 'individual' && org.firstname && org.lastname) {
    return `${org.firstname} ${org.lastname}`;
  }

  return org.orgname || org.name;
}

export function OwnerCellContents(props: Props): null | React.MixedElement {
  const {
    cartId,
    currentOrganization,
    productOwner,
    productOwnerOrg,
    product,
    cartCustomer,
  } = useOwner(props);
  const { __ } = useTranslator();

  const concernedProducts = [
    ...new Set([
      ...resellerProducts,
      ...toRenewProducts,
      ...attachedToDomainProducts,
      ...otherProducts,
    ]),
  ];

  // No need for an OwnerCell for products
  // which do not need ownership clarification.
  if (!product || !concernedProducts.includes(product.component)) {
    return null;
  }

  if (!currentOrganization) {
    return null;
  }

  const accountChoiceLink = `/cart/${cartId}/configure/account-choice`;

  // $FlowIgnore[prop-missing] FIXME: not the same Organization, one is from `redux-sharing` the other from `redux-organization` libs
  let ownerName = getOrgDisplayName(currentOrganization);

  // Fallback for a reseller buying a product for a customer.
  // In case `payload.owner_name` hasn't been correctly set.
  if (
    currentOrganization.reseller &&
    cartCustomer &&
    resellerProducts.includes(product.component)
  ) {
    ownerName = __('your customer');
  }

  // Set by account-choice in case of a reseller buying for a customer.
  if (product.payload.owner_name) {
    ownerName = product.payload.owner_name;
  }

  // Case of buying for someone else.
  // XXX: do we need to change the condition to allow `productOwner || productOwnerOrg`
  if (productOwner && otherProducts.includes(product.component)) {
    // $FlowIgnore[incompatible-call]
    ownerName = getOrgDisplayName(productOwnerOrg) || productOwner;
  }

  if (toRenewProducts.includes(product.component)) {
    return <span>{__('Owners will remain the same')}</span>;
  }

  if (attachedToDomainProducts.includes(product.component)) {
    return <span>{__('Owners is same as attached domain')}</span>;
  }

  return <OwnerCellContainer ownerName={ownerName} to={accountChoiceLink} />;
}

/** Similar to `SetupPill`, but the `icon` is optional. */
function OwnerCellContainer({
  children,
  ownerName,
  to,
}: {
  /** Full content of the Pill (if defined, only the `children` will be rendered, without an `icon`, link or owner) */
  children?: React.MixedElement | string,
  /** Owner */
  ownerName?: ?string,
  /** Link to use on the Pill (if not in `children` mode). Will use a `pen` icon if defined, or a `lock` icon otherwise. */
  to?: string,
}): React.MixedElement {
  const { __ } = useTranslator();

  let content = children ?? <TextPlaceholder />;

  if (!children && ownerName) {
    content = ownerName;

    content = mapTags(__('Owner: [[ownerName:]]'), () => ({
      ownerName: content,
    }));
  }

  // No icon
  if (children)
    return (
      <PillButton to={to} disabled={!to}>
        {content}
      </PillButton>
    );

  // With icon
  return (
    <SetupPill to={to} disabled={!to}>
      {content}
    </SetupPill>
  );
}
